import apiClient from './axios';
import type {
  TenantDto,
  PlanDto,
  TenantSubscriptionDto,
  SaasProductDto,
  MachineBindingDto,
  TenantCreateInput,
  TenantUpdateInput,
  PlanAssignInput,
  PlanCreateInput,
  PlanUpdateInput,
  SaasProductCreateInput,
  SaasProductUpdateInput,
  TenantSubscriptionUpdateInput,
  DashboardStatsDto,
} from '@cms/models';

/** Tenant subscription with optional relation payloads from API. */
export type TenantSubscriptionWithRelations = TenantSubscriptionDto & {
  plan?: PlanDto;
  tenant?: TenantDto;
};

/** Response from POST /licensing/check */
interface LicensingCheckResponse {
  allowed: boolean;
  reason: 'OK' | 'TENANT_SUSPENDED' | 'NO_SUBSCRIPTION' | 'MACHINE_MISMATCH';
  productCode: string;
}
import { getOrCreateMachineId } from '../utils/machine-id';

type Tenant = TenantDto;
type Plan = PlanDto;
type TenantSubscription = TenantSubscriptionDto & { plan?: PlanDto };
type SaasProduct = SaasProductDto;
type MachineBinding = MachineBindingDto;

export const saasApi = {
  /** Dashboard KPI counts. Optional tenantId filters products and subscriptions by tenant. */
  async getDashboardStats(tenantId?: string): Promise<DashboardStatsDto> {
    const params = tenantId ? { tenantId } : {};
    const { data } = await apiClient.get<DashboardStatsDto>('/dashboard/stats', {
      params,
    });
    return data;
  },

  async getTenants(): Promise<Tenant[]> {
    const { data } = await apiClient.get<Tenant[]>('/tenants');
    return Array.isArray(data) ? data : [];
  },

  async createTenant(body: TenantCreateInput): Promise<Tenant> {
    const { data } = await apiClient.post<Tenant>('/tenants', body);
    return data;
  },

  async updateTenant(id: string, body: TenantUpdateInput): Promise<Tenant> {
    const { data } = await apiClient.patch<Tenant>(`/tenants/${id}`, body);
    return data;
  },

  async suspendTenant(id: string): Promise<Tenant> {
    const { data } = await apiClient.post<Tenant>(`/tenants/${id}/suspend`);
    return data;
  },

  async activateTenant(id: string): Promise<Tenant> {
    const { data } = await apiClient.post<Tenant>(`/tenants/${id}/activate`);
    return data;
  },

  async deleteTenant(id: string): Promise<void> {
    await apiClient.delete(`/tenants/${id}`);
  },

  async getPlans(): Promise<Plan[]> {
    const { data } = await apiClient.get<Plan[]>('/plans');
    return Array.isArray(data) ? data : [];
  },

  async getPlanById(id: string): Promise<Plan> {
    const { data } = await apiClient.get<Plan>(`/plans/${id}`);
    return data;
  },

  async createPlan(body: PlanCreateInput): Promise<Plan> {
    const { data } = await apiClient.post<Plan>('/plans', body);
    return data;
  },

  async updatePlan(id: string, body: PlanUpdateInput): Promise<Plan> {
    const { data } = await apiClient.patch<Plan>(`/plans/${id}`, body);
    return data;
  },

  async deletePlan(id: string): Promise<void> {
    await apiClient.delete(`/plans/${id}`);
  },

  async assignPlanToTenant(tenantId: string, body: PlanAssignInput): Promise<TenantSubscription> {
    const { data } = await apiClient.post<TenantSubscription>(`/plans/tenants/${tenantId}/assign`, body);
    return data;
  },

  async getCurrentSubscription(tenantId: string): Promise<TenantSubscription | null> {
    const { data } = await apiClient.get<TenantSubscription | null>(`/plans/tenants/${tenantId}/current`);
    return data ?? null;
  },

  /** List tenant subscriptions (tenant assigned plan with end date). Optional tenantId filter. */
  async getTenantSubscriptions(tenantId?: string): Promise<TenantSubscriptionWithRelations[]> {
    const params = tenantId ? { tenantId } : {};
    const { data } = await apiClient.get<TenantSubscriptionWithRelations[]>('/plans/tenant-subscriptions', {
      params,
    });
    return Array.isArray(data) ? data : [];
  },

  /** Update tenant subscription (e.g. deactivate, set endsAt). */
  async updateTenantSubscription(
    id: string,
    body: TenantSubscriptionUpdateInput,
  ): Promise<TenantSubscriptionWithRelations> {
    const { data } = await apiClient.patch<TenantSubscriptionWithRelations>(
      `/plans/tenant-subscriptions/${id}`,
      body,
    );
    return data;
  },

  async getSaasProducts(tenantId?: string): Promise<SaasProduct[]> {
    const params = tenantId ? { tenantId } : {};
    const { data } = await apiClient.get<SaasProduct[]>('/products', {
      params,
    });
    return Array.isArray(data) ? data : [];
  },

  async createSaasProduct(body: SaasProductCreateInput): Promise<SaasProduct> {
    const { data } = await apiClient.post<SaasProduct>('/products', body);
    return data;
  },

  async updateSaasProduct(id: string, body: SaasProductUpdateInput): Promise<SaasProduct> {
    const { data } = await apiClient.patch<SaasProduct>(`/products/${id}`, body);
    return data;
  },

  async deleteSaasProduct(id: string): Promise<void> {
    await apiClient.delete(`/products/${id}`);
  },

  async getMachineBinding(userId: string, tenantId: string): Promise<MachineBinding | null> {
    try {
      const { data } = await apiClient.get<MachineBinding | null>('/machine-bindings', {
        params: { userId, tenantId },
      });
      return data ?? null;
    } catch {
      return null;
    }
  },

  async bindMachine(userId: string, tenantId: string, machineId: string): Promise<MachineBinding> {
    const body = { userId, tenantId, machineId };
    const { data } = await apiClient.post<MachineBinding>('/machine-bindings', body);
    return data;
  },

  async resetMachine(userId: string, tenantId: string): Promise<void> {
    await apiClient.delete('/machine-bindings', {
      params: { userId, tenantId },
    });
  },

  /**
   * Check licensing for a product (end-user). Uses JWT + machineId from localStorage.
   * Returns status and reason (OK, NO_SUBSCRIPTION, MACHINE_MISMATCH, TENANT_SUSPENDED).
   */
  async checkLicensing(productCode: string): Promise<LicensingCheckResponse> {
    const machineId = getOrCreateMachineId();
    const { data } = await apiClient.post<LicensingCheckResponse>(
      '/licensing/check',
      { productCode, machineId }
    );
    return data;
  },
};
